import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, AlertTriangle, ImageOff, TagIcon, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import { buildShapePath } from '@/lib/imagePaths'
import { shapesApi, graphicName } from '@/features/shapes/api/shapes'
import { GraphicUploadForm } from '@/features/shapes/components/GraphicUploadForm'
import { classificationsApi, type Classification } from '../api'
import { useCan } from '@/hooks/useCan'

/**
 * Which graphics a classification may draw, and which of them is its default.
 *
 * NO MANUFACTURER IS ASKED FOR HERE, and that is the whole point of this screen's shape. The
 * library captures a category and free-text tags when artwork is uploaded, and nothing else — so
 * asking here which vendor a graphic is "for" invented a second vocabulary that the library had
 * no way to fill. A candidate is chosen at render time by scoring ITS OWN TAGS against each
 * object's text (description, manufacturer, model, technology category). Assigning it is
 * therefore only a question of which graphics this classification may use.
 *
 * Which makes the tags the load-bearing thing on this screen, so they are shown against every
 * row. A candidate tagged only "Server" inside Physical Server will never be drawn — every
 * object there says "server", so the word carries no information and the matcher's confidence
 * gate correctly refuses it. Seeing the tags is the only way to notice that before wondering why
 * the canvas never changes.
 *
 * THE DEFAULT IS THE POINT. Hybrid mode draws the graphic INSTEAD of the information node, so a
 * classification with no default leaves every object whose text matches nothing with a
 * placeholder instead of equipment. The dialog says so rather than silently saving it, but does
 * not refuse: part-way through assigning is a real state, and losing the work to a validation
 * error would be worse than a warning.
 */
export function ClassificationGraphicsDialog({
    classification, open, onOpenChange,
}: {
    classification: Classification | null
    open: boolean
    onOpenChange: (o: boolean) => void
}) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()

    // Local until saved. The dialog writes the whole set in one PUT, so an abandoned edit leaves
    // nothing behind.
    const [rows, setRows] = useState<{ ShapeId: number | null; IsDefault: boolean }[]>([])

    const [uploadOpen, setUploadOpen] = useState(false)
    // Adding artwork to the library is graphics.create, a permission of its own: assigning what is
    // there (classifications.update) does not bring it.
    const canUpload = useCan('graphics.create')

    const { data: shapes = [] } = useQuery({
        queryKey: ['shapes', 'all'],
        queryFn: async () => (await shapesApi.list()).data.data,
        enabled: open,
    })
    // No `= []` default: a fresh array on every render re-ran the seeding effect below on every
    // render until the answer came, each run setting the rows again. On a cold cache that is a
    // render loop, and in a test it starved the request it was waiting for.
    const { data: allGraphics } = useQuery({
        queryKey: ['classifications', 'graphics'],
        queryFn: async () => (await classificationsApi.graphics()).data.data,
        enabled: open,
    })

    // Seed from the server every time the dialog opens on a classification, so reopening after a
    // cancel shows what is stored rather than the abandoned edit. Not before the answer is in:
    // seeding from nothing would show an empty row and a "no default" warning that are not true.
    useEffect(() => {
        if (!open || !classification || !allGraphics) return
        const mine = allGraphics.filter((g) => g.ClassificationID === classification.id)
        setRows(
            mine.length > 0
                ? mine.map((g) => ({ ShapeId: g.ShapeId, IsDefault: !!g.IsDefault }))
                : [{ ShapeId: null, IsDefault: true }],
        )
    }, [open, classification, allGraphics])

    const save = useMutation({
        mutationFn: async () => {
            if (!classification) return
            const payload = rows
                .filter((r) => r.ShapeId != null)
                .map((r) => ({ ShapeId: r.ShapeId as number, IsDefault: r.IsDefault }))
            await classificationsApi.setGraphics(classification.id, payload)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['classifications', 'graphics'] })
            toast({ title: t('classifications.graphicsSaved', 'Graphics saved') })
            onOpenChange(false)
        },
        onError: (e) => {
            // handleApiError returns a message string, not an object — same call shape the list
            // page uses.
            toast({ title: t('common.error', 'Error'), description: handleApiError(e), variant: 'destructive' })
        },
    })

    const shapeById = useMemo(() => new Map(shapes.map((s) => [s.id, s])), [shapes])

    // Only the graphics filed under this classification's category, by name. The whole library used
    // to be offered, same category first; Andrew, 2026-09-22: "it should only show graphics in that
    // category and it needs to show a thumbnail to the left of the name". A graphic's category is
    // where it is offered, and uploading from here files it under this category.
    const categoryShapes = useMemo(() => {
        if (!classification) return []
        return shapes
            .filter((s) => s.category === classification.Category)
            .sort((a, b) => graphicName(a).localeCompare(graphicName(b)))
    }, [shapes, classification])

    const usedShapes = new Set(rows.map((r) => r.ShapeId).filter((s): s is number => s != null))
    const usedInCategory = categoryShapes.filter((s) => usedShapes.has(s.id)).length
    const hasDefault = rows.some((r) => r.IsDefault && r.ShapeId != null)

    if (!open || !classification) return null

    const setRow = (i: number, patch: Partial<{ ShapeId: number | null; IsDefault: boolean }>) =>
        setRows((rs) => rs.map((r, ix) => (ix === i ? { ...r, ...patch } : r)))

    // Exactly one default, enforced in the UI as well as by the unique index — a radio that let
    // you pick a second would save, fail on 1062, and lose the whole set.
    const makeDefault = (i: number) =>
        setRows((rs) => rs.map((r, ix) => ({ ...r, IsDefault: ix === i })))

    return (
        <>
            <div className="fixed inset-0 z-[199] bg-black/20" onClick={() => onOpenChange(false)} aria-hidden />
            <div className="fixed left-1/2 top-1/2 z-[200] w-[680px] max-w-[94vw] max-h-[88vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background py-4 px-5 shadow-xl space-y-4">
                <div>
                    <h2 className="text-sm font-semibold">
                        {t('classifications.graphicsTitle', 'Graphics for {{name}}', { name: classification.Name })}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                        {t('classifications.graphicsHelpTags',
                            'The default is drawn whenever nothing else matches. Any other graphic here is a candidate — it is chosen for an object when its tags match that object’s technology, manufacturer or description.')}
                    </p>
                </div>

                {allGraphics && !hasDefault && (
                    <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs">
                        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                        <span>
                            {t('classifications.graphicsNoDefault',
                                'No default set. Objects of this classification whose tags match nothing will draw a placeholder instead of a graphic.')}
                        </span>
                    </div>
                )}

                <div className="space-y-2">
                    {rows.map((row, i) => {
                        const shape = row.ShapeId != null ? shapeById.get(row.ShapeId) : undefined
                        // A row keeps showing what it holds even if that graphic is filed under
                        // another category (none are today), labelled with it, rather than
                        // appearing empty. It is not offered to any other row.
                        const options = shape && shape.category !== classification.Category
                            ? [shape, ...categoryShapes]
                            : categoryShapes
                        return (
                            <div key={i} className="flex items-center gap-3 rounded-md border p-2">
                                <div className="h-14 w-14 shrink-0 rounded border bg-muted/30 grid place-items-center overflow-hidden">
                                    {shape
                                        ? <img src={buildShapePath(shape.filename)} alt="" className="h-full w-full object-contain" />
                                        : <ImageOff className="h-4 w-4 text-muted-foreground" />}
                                </div>

                                <div className="flex-1 min-w-0 space-y-1">
                                    <Select
                                        value={row.ShapeId != null ? String(row.ShapeId) : ''}
                                        onValueChange={(v) => setRow(i, { ShapeId: parseInt(v, 10) })}
                                    >
                                        <SelectTrigger className="h-8 text-xs">
                                            <SelectValue placeholder={t('classifications.graphicsPick', 'Choose a graphic')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {options.map((s) => (
                                                <SelectItem
                                                    key={s.id}
                                                    value={String(s.id)}
                                                    // Already on another row — the unique index would
                                                    // refuse it, so do not offer it.
                                                    disabled={s.id !== row.ShapeId && usedShapes.has(s.id)}
                                                    // The picture, left of its name, in the list only:
                                                    // the row already shows the chosen one large.
                                                    leading={
                                                        <span className="mr-2 grid h-7 w-12 shrink-0 place-items-center overflow-hidden rounded-sm border bg-muted/30">
                                                            <img
                                                                src={buildShapePath(s.filename)}
                                                                alt=""
                                                                loading="lazy"
                                                                className="h-full w-full object-contain"
                                                            />
                                                        </span>
                                                    }
                                                >
                                                    {/* The NAME, not the file. A filename is where the
                                                        bytes live; it is not what anyone calls the
                                                        thing, and "coreswitch.png" reads as plumbing
                                                        in a list you are choosing artwork from. */}
                                                    {graphicName(s)}
                                                    {s.category !== classification.Category && (
                                                        <span className="text-muted-foreground"> · {s.category}</span>
                                                    )}
                                                </SelectItem>
                                            ))}
                                            {options.length === 0 && (
                                                <p className="px-3 py-2 text-xs text-muted-foreground">
                                                    {t('classifications.graphicsNoneInCategory',
                                                        'No graphics in {{category}} yet. Upload one below.',
                                                        { category: classification.Category })}
                                                </p>
                                            )}
                                        </SelectContent>
                                    </Select>

                                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground min-h-[16px]">
                                        <TagIcon className="h-3 w-3 shrink-0" aria-hidden />
                                        {shape?.tags
                                            ? <span className="truncate">{shape.tags}</span>
                                            : <span className="italic">
                                                {t('classifications.graphicsNoTags', 'No tags — this graphic can only ever be the default')}
                                            </span>}
                                    </div>
                                </div>

                                <Label className="flex shrink-0 items-center gap-1.5 text-[11px] cursor-pointer">
                                    <input
                                        type="radio"
                                        name="classification-default-graphic"
                                        checked={row.IsDefault}
                                        disabled={row.ShapeId == null}
                                        onChange={() => makeDefault(i)}
                                        className="h-3.5 w-3.5"
                                    />
                                    {t('classifications.graphicsDefault', 'Default')}
                                </Label>

                                <Button
                                    variant="ghost" size="icon" className="h-8 w-8 shrink-0"
                                    onClick={() => setRows((rs) => rs.filter((_, ix) => ix !== i))}
                                    title={t('common.delete', 'Delete')}
                                >
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            </div>
                        )
                    })}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline" size="sm"
                        onClick={() => setRows((rs) => [...rs, { ShapeId: null, IsDefault: rs.length === 0 }])}
                        // Every graphic in this category is already assigned; another row could
                        // only duplicate one.
                        disabled={usedInCategory >= categoryShapes.length}
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        {t('classifications.graphicsAdd', 'Add graphic')}
                    </Button>
                    {canUpload && (
                        <Button variant="ghost" size="sm" onClick={() => setUploadOpen((o) => !o)}>
                            <Upload className="mr-2 h-4 w-4" />
                            {t('classifications.graphicsUploadNew', 'Upload a new graphic')}
                        </Button>
                    )}
                </div>

                {canUpload && uploadOpen && (
                    <div className="rounded-md border border-dashed p-3">
                        {/* THE SAME FORM the library screen uses, not a smaller copy of it. Two
                            forms for one operation means the graphic you get depends on where you
                            were standing — and the copy here asked only for a name and tags, so
                            anything uploaded from this dialog arrived with no description, which
                            is the NAME every other screen displays it by. */}
                        <GraphicUploadForm
                            fixedCategory={classification.Category}
                            submitLabel={t('classifications.graphicsUploadAction', 'Upload and use')}
                            onCancel={() => setUploadOpen(false)}
                            onUploaded={(created) => {
                                // Straight onto a row — "upload and use" is the whole point — as
                                // the default if this classification has not got one yet.
                                setRows((rs) => {
                                    const empty = rs.findIndex((r) => r.ShapeId == null)
                                    const entry = {
                                        ShapeId: created.id,
                                        IsDefault: !rs.some((r) => r.IsDefault && r.ShapeId != null),
                                    }
                                    return empty >= 0
                                        ? rs.map((r, i) => (i === empty ? entry : r))
                                        : [...rs, entry]
                                })
                                setUploadOpen(false)
                            }}
                        />
                    </div>
                )}

                <div className="flex justify-end gap-2 border-t pt-3">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={() => save.mutate()} disabled={save.isPending}>
                        {save.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                    </Button>
                </div>
            </div>
        </>
    )
}
